import React, { useMemo, useState } from 'react';
import type { FormInstance } from 'antd';
import { Button, Card, Form, InputNumber, Select, Space, Tabs, Tooltip } from 'antd';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { asPreviewCss } from '../utils/bookDefaultTemplates';
import { SectionLabel, StyleFieldGroup } from './common/IconFormLabel';
import { LineStyleEditTabs } from './karmakanda/LineStyleEditCollapse';
import './karmakanda/StyleModalTabs.css';

type Props = {
  form: FormInstance;
  previewTitle?: string;
};

export const BookDefaultStylesEditor: React.FC<Props> = ({ form, previewTitle }) => {
  const [activeStyleTab, setActiveStyleTab] = useState('page');

  const headerTextColor = Form.useWatch(['defaultHeaderStyle', 'color'], form);
  const headerBgColor = Form.useWatch(['defaultHeaderStyle', 'backgroundColor'], form);
  const headerBorderColor = Form.useWatch(['defaultHeaderStyle', 'borderColor'], form);

  const lineTextColor = Form.useWatch(['defaultLineStyle', 'color'], form);
  const lineBgColor = Form.useWatch(['defaultLineStyle', 'backgroundColor'], form);
  const lineBorderColor = Form.useWatch(['defaultLineStyle', 'borderColor'], form);

  const nestedLineTextColor = Form.useWatch(['defaultNestedLineStyle', 'color'], form);
  const nestedLineBgColor = Form.useWatch(['defaultNestedLineStyle', 'backgroundColor'], form);
  const nestedLineBorderColor = Form.useWatch(['defaultNestedLineStyle', 'borderColor'], form);

  const deepNestedLineTextColor = Form.useWatch(['defaultDeepNestedLineStyle', 'color'], form);
  const deepNestedLineBgColor = Form.useWatch(['defaultDeepNestedLineStyle', 'backgroundColor'], form);
  const deepNestedLineBorderColor = Form.useWatch(['defaultDeepNestedLineStyle', 'borderColor'], form);

  const linkTextColor = Form.useWatch(['defaultLinkStyle', 'color'], form);
  const linkBgColor = Form.useWatch(['defaultLinkStyle', 'backgroundColor'], form);
  const linkBorderColor = Form.useWatch(['defaultLinkStyle', 'borderColor'], form);

  const contentLineGapPx = Form.useWatch('contentLineGapPx', form);
  const contentAlign = Form.useWatch('contentAlign', form);

  const openSection = (tabKey: string) => {
    setActiveStyleTab(tabKey);
  };

  const previewFlowStyle = useMemo((): React.CSSProperties => {
    const gap = Math.max(0, Number(contentLineGapPx) || 0);
    const align = contentAlign === 'center' || contentAlign === 'right' || contentAlign === 'justify'
      ? contentAlign
      : 'left';
    const style: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap,
      lineHeight: 1,
    };
    if (align === 'center') {
      style.textAlign = 'center';
      style.alignItems = 'center';
    } else if (align === 'right') {
      style.textAlign = 'right';
      style.alignItems = 'flex-end';
    } else if (align === 'justify') {
      style.textAlign = 'justify';
      style.alignItems = 'stretch';
    } else {
      style.textAlign = 'left';
      style.alignItems = 'stretch';
    }
    return style;
  }, [contentLineGapPx, contentAlign]);

  const pageTab = (
    <div className="style-modal-tab-panel">
      <StyleFieldGroup className="style-field-group--page-settings">
        <Tooltip title="Gap between each line of text in the reader">
          <Space size={6}>
            {SectionLabel.spacing}
            <Form.Item name="contentLineGapPx" style={{ marginBottom: 0 }}>
              <InputNumber size="small" min={0} max={48} step={1} style={{ width: 72 }} aria-label="Line gap" />
            </Form.Item>
          </Space>
        </Tooltip>
        <Tooltip title="Align all content left, center, right, or justified">
          <Space size={6}>
            {SectionLabel.layout}
            <Form.Item name="contentAlign" style={{ marginBottom: 0 }}>
              <Select
                size="small"
                style={{ width: 108 }}
                aria-label="Content align"
                options={[
                  { label: 'Left', value: 'left' },
                  { label: 'Center', value: 'center' },
                  { label: 'Right', value: 'right' },
                  { label: 'Justify', value: 'justify' },
                ]}
              />
            </Form.Item>
          </Space>
        </Tooltip>
      </StyleFieldGroup>

      <Form.Item noStyle shouldUpdate>
        {() => {
          const headerStyle = form.getFieldValue('defaultHeaderStyle');
          const lineStyle = form.getFieldValue('defaultLineStyle');
          const nestedLineStyle = form.getFieldValue('defaultNestedLineStyle');
          const deepNestedLineStyle = form.getFieldValue('defaultDeepNestedLineStyle');
          const linkStyle = form.getFieldValue('defaultLinkStyle');

          return (
            <div>
              <Tooltip title={previewTitle || 'Preview — use pencil icons to jump to a section'}>
                <EyeOutlined style={{ fontSize: 14, color: '#605e5c', marginBottom: 4 }} aria-label="Preview" />
              </Tooltip>
              <Card size="small" styles={{ body: { padding: '8px 10px' } }}>
                <div style={previewFlowStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={asPreviewCss(headerStyle)}>Header / Section</span>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSection('headerAdvanced')} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={asPreviewCss(lineStyle)}>
                      This is a sample line with a <span style={asPreviewCss(linkStyle)}>link</span>.
                    </span>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSection('lineAdvanced')} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={asPreviewCss(nestedLineStyle)}>Nested line (level 1)</span>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSection('nestedLineAdvanced')} />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginLeft: contentAlign === 'center' || contentAlign === 'right' ? 0 : 20,
                    }}
                  >
                    <span style={asPreviewCss(deepNestedLineStyle)}>Nested line (level 2+)</span>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSection('deepNestedLineAdvanced')} />
                  </div>
                </div>
              </Card>
            </div>
          );
        }}
      </Form.Item>
    </div>
  );

  return (
    <Tabs
      className="style-modal-tabs"
      size="small"
      type="card"
      activeKey={activeStyleTab}
      onChange={setActiveStyleTab}
      destroyOnHidden={false}
      items={[
        { key: 'page', label: SectionLabel.page, children: pageTab },
        {
          key: 'headerAdvanced',
          label: SectionLabel.header,
          children: (
            <LineStyleEditTabs
              form={form}
              namePrefix="defaultHeaderStyle"
              mode="header"
              colors={{
                color: headerTextColor,
                backgroundColor: headerBgColor,
                borderColor: headerBorderColor,
              }}
            />
          ),
        },
        {
          key: 'lineAdvanced',
          label: SectionLabel.line,
          children: (
            <LineStyleEditTabs
              form={form}
              namePrefix="defaultLineStyle"
              showSpan
              colors={{
                color: lineTextColor,
                backgroundColor: lineBgColor,
                borderColor: lineBorderColor,
              }}
            />
          ),
        },
        {
          key: 'nestedLineAdvanced',
          label: SectionLabel.nestedLine,
          children: (
            <LineStyleEditTabs
              form={form}
              namePrefix="defaultNestedLineStyle"
              colors={{
                color: nestedLineTextColor,
                backgroundColor: nestedLineBgColor,
                borderColor: nestedLineBorderColor,
              }}
            />
          ),
        },
        {
          key: 'deepNestedLineAdvanced',
          label: SectionLabel.deepNestedLine,
          children: (
            <LineStyleEditTabs
              form={form}
              namePrefix="defaultDeepNestedLineStyle"
              colors={{
                color: deepNestedLineTextColor,
                backgroundColor: deepNestedLineBgColor,
                borderColor: deepNestedLineBorderColor,
              }}
            />
          ),
        },
        {
          key: 'linkAdvanced',
          label: SectionLabel.links,
          children: (
            <LineStyleEditTabs
              form={form}
              namePrefix="defaultLinkStyle"
              colors={{
                color: linkTextColor,
                backgroundColor: linkBgColor,
                borderColor: linkBorderColor,
              }}
            />
          ),
        },
      ]}
    />
  );
};
